import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { PLACEHOLDER_ARTICULO_GRANDE } from './utils/placeholders';
import AudioPlayer from './components/AudioPlayer'; // ← MANTENER REPRODUCTOR EXISTENTE
import SearchModal from './components/SearchModal';
import ShareIcon from './components/ShareIcon';
import { shareArticle, updateMetaTags } from './utils/shareUtils';
import { useFavorites } from './hooks/useFavorites';

const ArticleDetailView = ({ article, sectionTitle, onBack, allArticles = {}, onArticleClick, onSectionClick }) => {
  // Estado para el modal de búsqueda
  const [showSearchModal, setShowSearchModal] = useState(false);
  // Estado para mensaje de compartir
  const [shareMessage, setShareMessage] = useState(null);
  
  // Hook de favoritos
  const { isFavorite, toggleFavorite } = useFavorites();
  const [isFav, setIsFav] = useState(false);

  // Verificar si el artículo actual es favorito
  useEffect(() => {
    if (article?.id) {
      setIsFav(isFavorite(article.id));
    }
  }, [article, isFavorite]);

  // Lógica de header que se oculta/muestra según dirección de scroll
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const SCROLL_THRESHOLD = 10;

  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    const diff = currentY - lastScrollY.current;

    if (Math.abs(diff) < SCROLL_THRESHOLD) return;

    if (diff > 0 && currentY > 80) {
      setHeaderVisible(false);
    } else if (diff < 0) {
      setHeaderVisible(true);
    }

    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setHeaderVisible(true);
    lastScrollY.current = 0;
  }, [article]);

  // Actualizar meta tags cuando se carga el artículo
  useEffect(() => {
    if (article && sectionTitle) {
      updateMetaTags(article, sectionTitle);
    }
  }, [article, sectionTitle]);

  // Función para compartir el artículo
  const handleShare = async () => {
    const result = await shareArticle(article, sectionTitle);
    
    if (result.success) {
      if (result.method === 'clipboard' || result.method === 'legacy') {
        // Mostrar mensaje de confirmación
        setShareMessage('🔗 Enlace copiado al portapapeles');
        setTimeout(() => setShareMessage(null), 3000);
      }
      // Si es native, el sistema ya muestra su propio feedback
    } else if (!result.cancelled) {
      setShareMessage('❌ Error al compartir');
      setTimeout(() => setShareMessage(null), 3000);
    }
  };

  // Función para marcar/desmarcar como favorito
  const handleToggleFavorite = () => {
    if (!article?.id) return;
    
    const newFavStatus = toggleFavorite(article.id);
    setIsFav(newFavStatus);
    
    // Mostrar mensaje de confirmación
    const message = newFavStatus 
      ? '⭐ Agregado a favoritos' 
      : '✓ Eliminado de favoritos';
    setShareMessage(message);
    setTimeout(() => setShareMessage(null), 2000);
  };
  // Función para limpiar y formatear HTML
  const formatContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    return htmlContent
      .replace(/<audio[^>]*>.*?<\/audio>/gis, '') // Remover audio elements
      .replace(/<iframe[^>]*(?:youtube|vimeo)[^>]*>.*?<\/iframe>/gis, '') // Remover iframes de video
      .replace(/Your browser does not support the audio element\./g, '')
      .replace(/<div class="photo top"[^>]*>.*?<\/div>/gis, '') // Remover contenedores de foto
      .replace(/<br class="clear"[^>]*>/gi, '')
      .trim();
  };

  // ✅ Detectar si estamos en Capacitor iOS (origin capacitor:// no soporta iframes de YouTube)
  const isCapacitorIOS = useMemo(() => {
    return window.Capacitor?.getPlatform?.() === 'ios' || 
           window.location.origin === 'capacitor://localhost';
  }, []);

  // ✅ Extraer ID de YouTube (para embed y fallback)
  const youtubeVideoId = useMemo(() => {
    if (!article.hasVideo || !article.videoUrl) return null;
    const videoUrl = article.videoUrl;
    const videoType = article.videoType;
    
    if (videoType === 'youtube' || videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const match = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([^&\n?#]+)/);
      return match ? match[1] : null;
    }
    return null;
  }, [article.hasVideo, article.videoUrl, article.videoType]);

  // ✅ Calcular embedUrl para todas las plataformas
  const embedUrl = useMemo(() => {
    if (!article.hasVideo || !article.videoUrl) return null;
    
    const videoUrl = article.videoUrl;
    const videoType = article.videoType;
    
    // YouTube
    if (youtubeVideoId) {
      if (isCapacitorIOS) {
        // iOS Capacitor: usar proxy page alojada en nuestro dominio (origin https:// válido)
        // Esto evita Error 153 causado por origin capacitor:// que YouTube rechaza
        return `https://www.appagustinos.es/youtube-embed.html?v=${youtubeVideoId}`;
      }
      // Android/PWA: iframe directo a YouTube (origin https:// funciona)
      return `https://www.youtube.com/embed/${youtubeVideoId}?playsinline=1&rel=0&modestbranding=1`;
    }
    
    // Vimeo (funciona en todas las plataformas)
    if (videoType === 'vimeo' || videoUrl.includes('vimeo.com')) {
      if (videoUrl.includes('player.vimeo.com')) {
        return videoUrl;
      }
      const videoId = videoUrl.match(/vimeo\.com\/([0-9]+)/);
      return videoId ? `https://player.vimeo.com/video/${videoId[1]}?playsinline=1&color=e62c19` : null;
    }
    
    return null;
  }, [article.hasVideo, article.videoUrl, article.videoType, youtubeVideoId, isCapacitorIOS]);

  // ✅ Calcular isDirectVideo con useMemo
  const isDirectVideo = useMemo(() => {
    return article.videoType === 'mp4' || 
           article.videoUrl?.includes('.mp4') || 
           article.videoUrl?.includes('bunny');
  }, [article.videoType, article.videoUrl]);

  // Formatear fecha correctamente
  const formatDate = (dateString, isSanto = false) => {
    if (!dateString) return 'Fecha no disponible';
    
    try {
      const date = new Date(dateString);
      
      if (isSanto) {
        // Para santos, mostrar solo día y mes como "Festividad: 28 de agosto"
        return `Festividad: ${date.toLocaleDateString('es-ES', {
          month: 'long',
          day: 'numeric'
        })}`;
      } else {
        // Para otros artículos, mostrar fecha completa
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  // Verificar si es un santo de la sección Santos Agustinos
  const esSantoAgustino = sectionTitle === 'Santos Agustinos';

  // Definir las secciones que pertenecen a Clave agustiniana
  const SECCIONES_CLAVE_AGUSTINIANA = [
    'Píldoras de San Agustín',
    'Habla San Agustín',
    'Orando con San Agustín',
    'Historia y personajes',
    'Sabías que…',
    'Reflexión agustiniana',
    'Domingo con San Agustín'
  ];

  // Verificar si la sección actual es parte de Clave agustiniana
  const estoyEnClaveAgustiniana = SECCIONES_CLAVE_AGUSTINIANA.includes(sectionTitle);

  // Obtener artículos de la misma sección
  const sectionArticles = useMemo(() => {
    return allArticles[sectionTitle] || [];
  }, [allArticles, sectionTitle]);

  // Obtener artículos relacionados (anteriores y posteriores por fecha)
  const relatedArticles = useMemo(() => {
    if (!article || !sectionArticles.length) return [];
    
    // Ordenar artículos por fecha
    const sortedArticles = [...sectionArticles].sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return dateB - dateA; // Más recientes primero
    });
    
    // Encontrar el índice del artículo actual
    const currentIndex = sortedArticles.findIndex(a => a.id === article.id);
    
    if (currentIndex === -1) {
      // Si no se encuentra, devolver los primeros artículos
      return sortedArticles.slice(0, 5).filter(a => a.id !== article.id);
    }
    
    // Obtener artículos antes y después del actual
    const related = [];
    
    // Agregar artículos posteriores (más recientes)
    if (currentIndex > 0) {
      related.push(...sortedArticles.slice(Math.max(0, currentIndex - 2), currentIndex));
    }
    
    // Agregar artículos anteriores (más antiguos)
    if (currentIndex < sortedArticles.length - 1) {
      related.push(...sortedArticles.slice(currentIndex + 1, Math.min(sortedArticles.length, currentIndex + 4)));
    }
    
    // Limitar a 5 artículos
    return related.slice(0, 5).filter(a => a.id !== article.id);
  }, [article, sectionArticles]);

  // Obtener secciones para "Explora más"
  const exploreSections = useMemo(() => {
    const redGradient = 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)';
    const allSections = [
      { name: 'Buenos días', icon: '🌅', color: redGradient, description: 'Reflexiones matutinas' },
      { name: 'Evangelio', icon: '📖', color: redGradient, description: 'Palabra de Dios' },
      { name: 'Clave agustiniana', icon: '🔑', color: redGradient, description: 'Pensamiento agustiniano', isClaveAgustiniana: true },
      { name: 'Te interesa', icon: '🌍', color: redGradient, description: 'Noticias y actualidad' },
      { name: 'Santos Agustinos', icon: '👼', color: redGradient, description: 'Santoral agustiniano' }
    ];
    
    // Filtrar secciones según la lógica especial
    return allSections
      .filter(section => {
        // 1. No mostrar la sección actual
        if (section.name === sectionTitle) return false;
        
        // 2. Si es "Clave agustiniana": solo mostrar si NO estamos en una subsección
        if (section.isClaveAgustiniana) {
          return !estoyEnClaveAgustiniana;
        }
        
        // 3. Para otras secciones: verificar que tengan artículos
        const articles = allArticles[section.name];
        return articles && articles.length > 0;
      })
      .slice(0, 4); // Limitar a 4 secciones
  }, [sectionTitle, allArticles, estoyEnClaveAgustiniana]);

  const { seccion: urlSeccion, id: urlId, slug: urlSlug } = useParams() || {};

  const seoTitle = article
    ? (article.subtitle
        ? `${article.title} - ${article.subtitle} | App Agustinos`
        : `${article.title} | App Agustinos`)
    : 'App Agustinos';

  const seoDescription = article
    ? (article.description || article.subtitle || article.title)
    : '';

  const seoImage = article?.imageUrl || 'https://www.appagustinos.es/logo512.png';

  const canonicalUrl = urlSeccion && urlId && urlSlug
    ? `https://www.appagustinos.es/${urlSeccion}/${urlId}/${urlSlug}`
    : null;

  return (
    <div className="app-container">
      {article && (
        <Helmet>
          <title>{seoTitle}</title>
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          <meta name="description" content={seoDescription} />

          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:image" content={seoImage} />
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          <meta property="og:site_name" content="App Agustinos" />
          {article.section && <meta property="article:section" content={article.section} />}
          {article.date && <meta property="article:published_time" content={new Date(article.date).toISOString()} />}

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={seoImage} />
        </Helmet>
      )}
      <div className="content-container">
      
      {/* Header fijo: se oculta al bajar, reaparece al subir */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        {/* ===== FIX iOS: Fondo extendido que cubre la safe area ===== */}
        <div 
          className="absolute inset-x-0 top-0"
          style={{
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
            height: 'calc(70px + env(safe-area-inset-top))',
            marginTop: 'calc(-1 * env(safe-area-inset-top))',
            zIndex: 1
          }}
        />
        
        {/* Header con navegación - FIX iOS: Respeta safe area */}
        <div 
          className="relative text-white px-5"
          style={{
            paddingTop: 'max(env(safe-area-inset-top), 0px)',
            paddingBottom: '15px',
            minHeight: 'calc(70px + env(safe-area-inset-top))',
            zIndex: 10,
            background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)'
          }}
        >
          <div className="flex items-center justify-between" style={{ paddingTop: '15px' }}>
            {/* Botón de atrás */}
            <button
              onClick={() => {
                if (onBack) {
                  onBack();
                } else {
                  console.warn('⚠ onBack no está definido');
                }
              }}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
            >
              ←
            </button>
            
            {/* Título de la sección */}
            <h1 className="text-lg font-semibold text-center flex-1 mx-4">
              {sectionTitle || 'Artículo'}
            </h1>
            
            {/* Botones de compartir y búsqueda */}
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
                title="Compartir artículo"
              >
                <ShareIcon size={20} className="text-white" />
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Abriendo búsqueda desde detalle de artículo');
                  setShowSearchModal(true);
                }}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white hover:bg-opacity-30 transition-all"
                title="Buscar"
              >
                🔍
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Espaciador que compensa la altura del header fijo */}
      <div style={{ height: 'calc(70px + env(safe-area-inset-top, 0px))' }} />

      {/* Hero Section - Altura responsiva */}
      <div className="relative h-80 md:h-96 lg:h-[32rem] overflow-hidden">
        <img 
          src={article.imageUrl || article.image || PLACEHOLDER_ARTICULO_GRANDE}
          alt={article.title}
          className="w-full h-full object-cover absolute top-0 left-0 z-10"
        />
        <div 
          className="absolute top-0 left-0 right-0 bottom-0 z-20"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)'
          }}
        />
        
        {/* Botón de favorito sobre la imagen */}
        <button
          onClick={handleToggleFavorite}
          className={`absolute bottom-5 right-5 z-40 w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md shadow-lg text-2xl hover:scale-110 transition-all ${
            isFav ? 'bg-yellow-500 bg-opacity-95' : 'bg-white bg-opacity-30'
          }`}
          title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
        >
          {isFav ? '⭐' : '☆'}
        </button>
        
        <div className="absolute bottom-5 left-5 right-20 z-30 text-white">
          <h1 
            className="text-2xl font-bold mb-3"
            style={{
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            {article.title}
          </h1>
          <div className="flex flex-col gap-1 opacity-90">
            <span className="text-red-300 font-semibold text-sm">
              {esSantoAgustino 
                ? formatDate(article.date, true)  // "Festividad: 28 de agosto"
                : `Escrito el ${formatDate(article.date, false)}`  // "Escrito el 28 de agosto de 2025"
              }
            </span>
            <span className="text-white text-sm">
              {article.author || 'Agustinos'}
            </span>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div 
        className="px-5 py-8 bg-white"
        style={{
          animation: 'slideInUp 0.6s ease'
        }}
      >
        {/* Content Meta */}
        <div className="text-center mb-8 pb-5 border-b border-gray-200">
          <div className="text-base text-gray-700 mb-2 font-semibold">
            {article.author || 'Agustinos'}
          </div>
          {article.subtitle && (
            <div className="text-sm text-gray-500 whitespace-pre-wrap">
              {article.subtitle}
            </div>
          )}
        </div>

        {/* ===== MANTENER REPRODUCTOR DE AUDIO EXISTENTE ===== */}
        {article.hasAudio && article.audioUrl && (!article.mediaPublishDate || new Date(article.mediaPublishDate) <= new Date()) && (
          <div className="mb-8">
            <AudioPlayer 
              audioUrl={article.audioUrl}
              title="Audio del artículo"
              showTitle={true}
              size="normal"
              theme="agustinos"
            />
          </div>
        )}

        {/* ===== REPRODUCTOR DE VIDEO ===== */}
        {article.hasVideo && article.videoUrl && (!article.mediaPublishDate || new Date(article.mediaPublishDate) <= new Date()) && (
          <div className="mb-8" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="bg-black rounded-xl overflow-hidden shadow-lg">
              {isDirectVideo ? (
                // ✅ Video MP4/Bunny nativo (funciona en todas las plataformas)
                <video 
                  width="100%" 
                  controls 
                  playsInline
                  className="w-full"
                  poster={article.videoThumbnail || article.imageUrl || article.image}
                >
                  <source src={article.videoUrl} type="video/mp4" />
                  Tu navegador no soporta la reproducción de video.
                </video>
              ) : embedUrl ? (
                // ✅ iframe para YouTube/Vimeo (todas las plataformas)
                // En iOS Capacitor: embedUrl apunta a proxy page en appagustinos.es (origin https:// válido)
                // En Android/PWA: embedUrl apunta directamente a youtube.com/embed
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, backgroundColor: '#000' }}>
                  <iframe
                    src={embedUrl}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                    title="Video del artículo"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000'
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="text-center mt-3">
              <span className="text-sm text-gray-600 font-medium">Video del artículo</span>
            </div>
          </div>
        )}

        {/* Article Content */}
        <div 
          className="prose max-w-none leading-relaxed"
          style={{ fontSize: '16px' }}
        >
          {article.content ? (
            <div 
              dangerouslySetInnerHTML={{ 
                __html: formatContent(article.content) 
              }}
              style={{
                lineHeight: '1.8',
                color: '#374151'
              }}
            />
          ) : article.description ? (
            <div>
              <p className="text-gray-800 leading-relaxed mb-4">
                {article.description}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Botón de compartir dentro del contenido */}
      <div className="bg-white px-5 py-6 text-center border-t border-gray-100">
        <button 
          onClick={handleShare}
          className="inline-flex items-center gap-2 py-3 px-6 rounded-xl text-base font-semibold text-center transition-all text-white modern-button hover:scale-105 shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
          }}
        >
          <ShareIcon size={20} className="text-white" />
          Compartir
        </button>
      </div>

      {/* Related Content Section */}
      <div 
        className="bg-gray-50 px-5 py-8"
        style={{
          animation: 'slideInUp 0.8s ease'
        }}
      >
        {/* Related Articles Carousel */}
        {relatedArticles.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
              📖 Más de {sectionTitle || 'esta sección'}
            </h2>
            <div 
              className="flex gap-4 overflow-x-auto custom-scrollbar pb-4"
              style={{
                scrollSnapType: 'x mandatory',
                scrollBehavior: 'smooth'
              }}
            >
              {relatedArticles.map((relatedArticle, index) => (
                <div 
                  key={relatedArticle.id || index}
                  className="modern-card flex-shrink-0 cursor-pointer hover:transform hover:-translate-y-1 transition-all"
                  style={{
                    width: '280px',
                    scrollSnapAlign: 'start'
                  }}
                  onClick={() => {
                    console.log(`📎 Navegando a artículo relacionado: ${relatedArticle.title}`);
                    if (onArticleClick) {
                      onArticleClick(relatedArticle, sectionTitle);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {relatedArticle.imageUrl || relatedArticle.image ? (
                      <img 
                        src={relatedArticle.imageUrl || relatedArticle.image}
                        alt={relatedArticle.title}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'w-16 h-16 rounded-xl flex items-center justify-center text-white flex-shrink-0';
                          fallback.style.background = 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)';
                          fallback.textContent = '📖';
                          e.target.parentNode.insertBefore(fallback, e.target);
                        }}
                      />
                    ) : (
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                        style={{
                          background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
                        }}
                      >
                        📖
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">
                        {relatedArticle.title}
                      </div>
                      <div className="text-xs text-gray-500">
                        {relatedArticle.date && new Date(relatedArticle.date).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  {(relatedArticle.description || relatedArticle.subtitle) && (
                    <div className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      {relatedArticle.description || relatedArticle.subtitle}
                    </div>
                  )}
                  {relatedArticle.hasAudio && (
                    <div className="mt-2">
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                        🎵 Audio disponible
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cross Navigation */}
        {exploreSections.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
              🧭 Explora más
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {exploreSections.map((section, index) => (
                <div 
                  key={section.name}
                  className="modern-card text-center cursor-pointer hover:transform hover:-translate-y-1 transition-all"
                  onClick={() => {
                    console.log(`🧭 Navegando a sección: ${section.name}`);
                    if (section.isClaveAgustiniana) {
                      // Para "En clave agustiniana", navegar a la vista especial
                      // Disparar evento para abrir Clave Agustiniana
                      const event = new CustomEvent('navigateToClaveAgustiniana');
                      window.dispatchEvent(event);
                    } else if (onSectionClick) {
                      onSectionClick(section.name);
                    }
                  }}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl text-white mx-auto mb-3"
                    style={{
                      background: section.color
                    }}
                  >
                    {section.icon}
                  </div>
                  <div className="text-sm font-semibold text-gray-800 mb-1">
                    {section.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {section.description}
                  </div>
                  {!section.isClaveAgustiniana && (
                    <div className="text-xs text-gray-400 mt-1">
                      {allArticles[section.name]?.length || 0} artículos
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Mensaje de confirmación de compartir */}
      {shareMessage && (
        <div 
          className="fixed left-1/2 transform -translate-x-1/2 z-50 animate-slide-down"
          style={{ 
            top: 'max(env(safe-area-inset-top, 20px) + 80px, 100px)',
            maxWidth: '90vw', 
            width: '400px' 
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl border-2 border-green-500 p-4 text-center">
            <p className="text-green-600 font-semibold">{shareMessage}</p>
          </div>
        </div>
      )}

      {/* Padding bottom para espacio */}
      <div style={{ height: '40px' }}></div>
      </div>

      {/* Modal de búsqueda */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        currentSection={sectionTitle}
        onArticleClick={(article, section) => {
          console.log('📎 Artículo seleccionado desde búsqueda:', article.title);
          // Navegar al artículo usando el callback proporcionado
          if (onArticleClick) {
            onArticleClick(article, section);
          }
        }}
      />
    </div>
  );
};

export default ArticleDetailView;